import { useDispatch, useSelector, withFullHydrate } from '@ecommerce/nest-ssr/common';
import { useSeo } from 'hooks/use-seo';
import React, { useEffect } from 'react';
import { modals } from '../../../../../reducers/modals';
import { offers, OffersState } from '../../../../../reducers/offers';
import AdultGuard from 'components/adult-guard/adult-guard';
import MainLayoutDesktop from 'layouts/main-layout/main-layout.desktop';
import OfferInterface from 'interfaces/offer.interface';
import ProductDescription from 'modules/product/views/components/product-description';
import ProductOffersHeaderDesktop from 'modules/product/views/components/product-offers-header/product-offers-header-desktop';
import ProductOpinionSection from 'modules/product/views/components/product-opinion-section';
import ProductTab from 'modules/product/views/components/product-tab';
import ProductTopSection from 'modules/product/views/components/product-top-section';
import BreadcrumbsDesktop from 'modules/shared/components/breadcrumbs/breadcrumbs.desktop';
import ProductAttributesTable from '../../components/product-attributes-table/product-attributes-table';
import ProductOffers from '../../components/product-offers/product-offers';
import ProductSpecificationDesktop from '../../components/product-specification/product-specification-desktop/product-specification-desktop';
import ProductTechnologies from '../../components/product-technologies/product-technologies';
import ProductWidgetSimilarProduct from '../../components/product-widget-similar-product/product-widget-similar-product';
import Container from 'atomic/container';
import ProductSectionBox from 'modules/product/views/components/product-section-box';
import SecondTitle from 'modules/shared/components/second-title';
import ProductDetailImpression from 'components/impressions/product-impression/detail/product-detail-impression';
import MetaTagsProduct from 'components/meta-tags/meta-tags-product/meta-tags-product';
import PriceAlertEmailModal from 'components/modal/price-alert-modal/price-alert-email-modal';
import updateProductUrlHistory from 'modules/product/helpers/update-product-url-history';
import ProductStructuralData from 'modules/product/views/components/product-structural-data/product-structural-data';
import ProductChips from 'modules/product/views/components/product-chips/product-chips';
import WidgetChipsDesktop from 'modules/shared/widgets/components/widget-chips/widget-chips-desktop';
import ProductProposed from 'modules/product/views/components/product-proposed';
import { ProductPageProps } from './product-page-props';
import RichContentList from 'modules/shared/components/rich-content-list';
import ProductComparisonDesktop from 'modules/product/views/components/product-comparison/product-comparison-desktop';
import { Head } from '@ecommerce/nest-ssr/components';
import Link from 'atomic/link';
import Icon from 'atomic/icon';
import { reportNoticeLink } from './data/content.data';
import { getProductPreviewPhotoURL } from 'modules/product/helpers/product-image';
import offersPrepare from 'helpers/offers-prepare';
import { ProductInterface } from 'modules/product/interfaces/product.interface';
import ProductQuestions from 'modules/product/views/components/product-questions-answers';
import getPromotedOffer from 'helpers/get-promoted-offer';
import Newsletter from 'components/newsletter';
import ProductOffersSimilar from 'modules/product/views/components/product-offers/product-offers-similar';
import { isShowSimilarOffers } from 'modules/product/views/components/product-offers/helpers/is-show-similar-offers';
import AdSlotPanel from 'components/ads/ad-slot-panel';
import ProductVariantsStructuralData from 'modules/product/views/components/product-variants-structural-data/product-variants-structural-data';
import { isFinancingSectionPromoted, isShowFinancing } from '../../components/product-financing/helpers/financing';
import ProductFinancingSection from 'modules/product/views/components/product-financing/product-financing-section';

import './index.desktop.scss';

const ProductNewLayoutDesktop: React.FC<ProductPageProps> = ({
    product,
    breadcrumbs,
    isAdultAccepted,
    productUrlParams,
    proposedProducts,
    productsSimilarPrice,
    promotedDealers
}) => {
    const seo = useSeo();
    const dispatch = useDispatch();
    const prepareOffers = useSelector<{ product: ProductInterface; }, OfferInterface[]>(state => [...offersPrepare(state.product.offers.items)]);
    const offers = useSelector<{ offers: OffersState; }, OfferInterface[]>(
        state => state.offers.prepared?.length ? [...state.offers.prepared] : prepareOffers
    );

    const promotedOffer = getPromotedOffer(offers, productUrlParams, promotedDealers?.promotedDealers);

    useEffect(() => {
        updateProductUrlHistory(productUrlParams, dispatch, prepareOffers);
    }, []);

    let preloadImageUrl = null;

    if (product?.photos?.items?.length > 0) {
        preloadImageUrl = getProductPreviewPhotoURL(product?.photos?.items[0]);
    }

    return (
        <MainLayoutDesktop>
            {Boolean(preloadImageUrl) && (
                <Head>
                    <link rel="preload" as="image" href={preloadImageUrl || ''}
                        // @ts-expect-error - ts doesn't support this attribute
                        // eslint-disable-next-line
                        fetchpriority="high"
                    />
                </Head>
            )}
            <AdultGuard isEnabled={product.category.restrictions.includes('adult')} isBlocked={!isAdultAccepted}>
                <div className="product">
                    <Container className="screening-bg" dataSection="product-details">
                        <BreadcrumbsDesktop breadcrumbs={breadcrumbs} />
                        <ProductTopSection
                            promotedDealers={promotedDealers}
                            product={product}
                            productOffers={{
                                items: offers,
                                count: offers.length
                            }}
                            promotedOffer={promotedOffer}
                        />
                    </Container>
                    <ProductTab
                        opinionsCount={product.opinions.count}
                        offersCount={offers.length}
                        producerBadges={product.producer?.config?.badges?.productPage}
                        isQuestions={product.questions?.length > 0}
                        isBankFinancing={isShowFinancing(promotedOffer, product)}
                    />
                    <Container className="product__content-main screening-bg">
                        <div className="compliance-notice">
                            INFORMACJA HANDLOWA
                        </div>
                        {isShowFinancing(promotedOffer, product) && isFinancingSectionPromoted(productUrlParams) && (
                            <ProductFinancingSection
                                productPrice={promotedOffer.price}
                                isMobile={false}
                            />
                        )}
                        <SecondTitle text={offers.length !== 0 ? 'Oferty' : proposedProducts?.[0]?.category.name ?? 'Inne'}>
                            {offers.length !== 0 ? `(${offers.length})` : '- najlepsze propozycje'}
                        </SecondTitle>
                        {product.offers.count !== 0 && (
                            <ProductOffersHeaderDesktop />
                        )}
                        <section id="ceny" className="tab-section" data-section="oferty">
                            {product.offers.count > 0 && (
                                <ProductOffers
                                    productName={seo.h1 || product.name}
                                    promotedDealers={promotedDealers?.promotedBestDealers}
                                />
                            )}
                            {product.offers.count === 0 && <ProductProposed proposedProducts={proposedProducts || []} />}
                        </section>
                        {isShowSimilarOffers(product) && (
                            <ProductOffersSimilar
                                productId={product.id}
                                productDealerId={product.offers.items[0].dealer.id}
                            />
                        )}
                        <div className="content-group">
                            <SecondTitle text="Opis i specyfikacja" />
                            <section id="opis" className="tab-section">
                                <ProductSectionBox headerText="Opis produktu" padding="lg">
                                    <ProductDescription
                                        className="product__description"
                                        description={product.description}
                                        expanded={true}
                                        anchorIdName={'opis-produktu'}
                                    />
                                </ProductSectionBox>
                            </section>
                            {product.richContentDictionary.length > 0 && (
                                <section id="slownik-pojec" className="tab-section">
                                    <ProductSectionBox headerText="Informacja o produkcie">
                                        <RichContentList richContentData={product.richContentDictionary} />
                                    </ProductSectionBox>
                                </section>
                            )}
                            {product.technologies.length > 0 && (
                                <section id="kluczowe-technologie" className="tab-section">
                                    <ProductSectionBox headerText="Kluczowe technologie" padding="lg">
                                        <ProductTechnologies technologies={product.technologies} />
                                    </ProductSectionBox>
                                </section>
                            )}
                            {product.attributes.length > 0 && (
                                <section id="attributes" className="tab-section">
                                    <ProductSectionBox headerText="Specyfikacja" boxView={true} padding="lg">
                                        <ProductAttributesTable
                                            attributes={product.attributes}
                                            showRichContentLink={product.richContentDictionary.length > 0}
                                        />
                                    </ProductSectionBox>
                                </section>
                            )}
                            {product.specification.length > 0 && (
                                <section id="specification" className="tab-section">
                                    <ProductSectionBox headerText="Specyfikacja szczegółowa" padding="lg">
                                        <ProductSpecificationDesktop specification={product.specification} />
                                    </ProductSectionBox>
                                </section>
                            )}
                        </div>
                        <section id="report-notice" className="notice-section">
                            <Link
                                href={reportNoticeLink.href}
                                target={reportNoticeLink.target}
                                rel={reportNoticeLink.rel}
                                title={reportNoticeLink.title}
                                size="md"
                                weight={reportNoticeLink.weight ?? '400'}
                                block={true}
                                noWrap={true}
                            >
                                {reportNoticeLink.icon && (
                                    <Icon name={reportNoticeLink.icon} color="secondary" size={20} />
                                )}
                                {reportNoticeLink.label}
                            </Link>
                        </section>
                        {isShowFinancing(promotedOffer, product) && !isFinancingSectionPromoted(productUrlParams) && (
                            <ProductFinancingSection
                                productPrice={promotedOffer.price}
                                isMobile={false}
                            />
                        )}
                        {product.offers.count > 0 && productsSimilarPrice?.products.length === 3 && productsSimilarPrice?.attributes.length > 2 && (
                            <>
                                <SecondTitle text="Porównaj z podobnymi" />
                                <section id="similar" className="tab-section" data-section="similar">
                                    <ProductSectionBox padding="lg">
                                        <ProductComparisonDesktop
                                            productName={product.name}
                                            productsSimilarPrice={productsSimilarPrice}
                                        />
                                    </ProductSectionBox>
                                </section>
                            </>
                        )}
                        {(product.moreLikeThis?.count ?? 0) > 0 && (
                            <section id="polecane" className="tab-section" data-section="polecane">
                                <ProductWidgetSimilarProduct
                                    products={product.moreLikeThis}
                                    categoryUrl={product.category.url}
                                    isMobile={false}
                                    productId={product.id}
                                />
                            </section>
                        )}
                        {product.questions?.length > 0 && (
                            <>
                                <SecondTitle text="Pytania i odpowiedzi" />
                                <section id="pytania" className="tab-section">
                                    <ProductSectionBox
                                        // eslint-disable-next-line max-len
                                        text={'W sekcji "Pytania i odpowiedzi" znajdują się najczęściej zadawane pytania dotyczące danego produktu wraz z odpowiedziami od naszego zespołu ekspertów. To źródło dodatkowych informacji, które pomogą Ci dokonać wyboru przed dokonaniem zakupu.'}
                                        headerText={`${seo.h1 || product.name} - pytania i odpowiedzi`}
                                        padding="lg"
                                    >
                                        <ProductQuestions
                                            offerLink={getPromotedOffer(offers, productUrlParams, promotedDealers?.promotedDealers)?.url}
                                            questions={product.questions}
                                        />
                                    </ProductSectionBox>
                                </section>
                            </>
                        )}
                        <SecondTitle text="Opinie o produkcie">
                            {' '}
                            {product.opinions.count !== 0 ? `(${product.opinions.count})` : void 0}
                        </SecondTitle>
                        <section id="opinie" className="tab-section" data-section="opinie">
                            <ProductSectionBox headerText="Opinie klientów" padding="lg">
                                <ProductOpinionSection product={product} />
                            </ProductSectionBox>
                        </section>
                    </Container>
                    <Container className="product__content-chips screening-bg">
                        <section id="check-also" className="tab-section tab-section__check-also--desktop" data-section="product-chips">
                            <ProductSectionBox headerText="Sprawdź także" padding="lg">
                                <ProductChips productChips={product.productChips} component={WidgetChipsDesktop} />
                            </ProductSectionBox>
                        </section>
                    </Container>
                    <Container>
                        <AdSlotPanel />
                    </Container>
                    <ProductStructuralData product={product} />
                    <ProductVariantsStructuralData product={product} />
                    <ProductDetailImpression product={product} />
                    <PriceAlertEmailModal />
                    <MetaTagsProduct
                        productId={product.id}
                        producerName={product.producer?.name || ''}
                        offersCount={product.offers.count}
                        categoryName={product.category.name}
                        categoryDepartmentName={product.category.path[0].category.name}
                        categorySubDepartmentName={product.category.path?.[1]?.category.name}
                        seoRobotsContent={seo.robotsContent}
                    />
                </div>
            </AdultGuard>
            <Newsletter isMobile={false} />
        </MainLayoutDesktop>
    );
};

export default withFullHydrate(ProductNewLayoutDesktop, {
    root: '#root',
    reducers: { offers, modals }
}, 'modules/product/views/pages/product/index.desktop');
